import { useState, KeyboardEvent } from "react"
import { Input } from "@/modules/shared/ui/components/input"
import { Badge } from "@/modules/shared/ui/components/badge"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/modules/shared/ui/components/select"
import { Button } from "@/modules/shared/ui/components/button"
import { Label } from "@/modules/shared/ui/components/label"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/modules/shared/ui/components/dialog"
import { X, Plus, RotateCcw, Pencil } from "lucide-react"
import { WeightedSkill } from "@/modules/recruitment/entities/WeightedSkill"

interface WeightedSkillInputProps {
    id?: string
    values: WeightedSkill[]
    onChange: (values: WeightedSkill[]) => void
    placeholder?: string
    weightPlaceholder?: string
    className?: string
}

export function WeightedSkillInput({
    id,
    values,
    onChange,
    placeholder,
    weightPlaceholder,
    className
}: WeightedSkillInputProps) {
    const [nameValue, setNameValue] = useState("")
    const [weightValue, setWeightValue] = useState<number>(1)
    const [editingIndex, setEditingIndex] = useState<number | null>(null)
    const [editingName, setEditingName] = useState("")
    const [editingWeight, setEditingWeight] = useState<number>(1)

    const addValue = () => {
        const trimmedName = nameValue.trim()
        if (!trimmedName) return

        if (values.some((item) => item.name.toLowerCase() === trimmedName.toLowerCase())) {
            setNameValue("")
            return
        }

        onChange([...values, { name: trimmedName, weight: weightValue }])
        setNameValue("")
        setWeightValue(1)
    }

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            e.preventDefault()
            addValue()
        }
    }

    const openEditDialog = (index: number) => {
        setEditingIndex(index)
        setEditingName(values[index].name)
        setEditingWeight(values[index].weight ?? 1)
    }

    const confirmEdit = () => {
        if (editingIndex === null) return
        const trimmedName = editingName.trim()
        if (!trimmedName) {
            closeEditDialog()
            return
        }

        const duplicate = values.some(
            (item, i) => i !== editingIndex && item.name.toLowerCase() === trimmedName.toLowerCase()
        )
        if (!duplicate) {
            onChange(values.map((item, index) =>
                index === editingIndex ? { ...item, name: trimmedName, weight: editingWeight } : item
            ))
        }
        closeEditDialog()
    }

    const closeEditDialog = () => {
        setEditingIndex(null)
        setEditingName("")
        setEditingWeight(1)
    }

    const removeValue = (indexToRemove: number) => {
        onChange(values.map((item, index) =>
            index === indexToRemove ? { ...item, deleted: true } : item
        ))
    }

    const reinstateValue = (indexToReinstate: number) => {
        onChange(values.map((item, index) =>
            index === indexToReinstate ? { ...item, deleted: false } : item
        ))
    }

    return (
        <div className={`space-y-2 ${className ?? ""}`}>
            <div className="flex flex-wrap gap-2">
                {values.map((value, index) => (
                    <Badge
                        key={`${value.name}-${index}`}
                        variant="secondary"
                        className={`px-2 py-1 transition-colors ${
                            value.deleted
                                ? "bg-destructive/10 text-destructive/60 line-through"
                                : "bg-primary/10 text-primary hover:bg-primary/20"}`}
                    >
                        {value.name}
                        {value.weight !== undefined && value.weight !== null ? ` (${value.weight})` : ""}
                        {!value.deleted && (
                            <button
                                type="button"
                                onClick={() => openEditDialog(index)}
                                className="ml-1 hover:text-primary"
                            >
                                <Pencil className="h-3 w-3" />
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={() => removeValue(index)}
                            disabled={value.deleted}
                            className={`ml-1 ${value.deleted ? "cursor-not-allowed opacity-40" : "hover:text-primary"}`}
                        >
                            <X className="h-3 w-3" />
                        </button>
                        {value.deleted && (
                            <button
                                type="button"
                                onClick={() => reinstateValue(index)}
                                className="ml-1 text-green-500 hover:text-green-600 transition-colors"
                            >
                                <RotateCcw className="h-3 w-3 stroke-[3]" />
                            </button>
                        )}
                    </Badge>
                ))}
            </div>
            <div className="flex gap-2 items-end">
                <Input
                    id={id}
                    type="text"
                    value={nameValue}
                    onChange={(e) => setNameValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={placeholder}
                />
                <div className="flex flex-col gap-1">
                    <Label className="text-xs text-muted-foreground">Weight</Label>
                    <Select
                        value={String(weightValue)}
                        onValueChange={(value) => setWeightValue(Number(value))}
                    >
                        <SelectTrigger className="w-28">
                            <SelectValue placeholder={weightPlaceholder ?? "Weight"} />
                        </SelectTrigger>
                        <SelectContent className="bg-card">
                            {[1, 2, 3, 4, 5].map((w) => (
                                <SelectItem key={w} value={String(w)}>{w}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <Button
                    type="button"
                    onClick={addValue}
                    size="icon"
                    disabled={!nameValue.trim()}
                    className="shrink-0 bg-primary hover:bg-primary/90 text-primary-foreground transition-colors"
                >
                    <Plus className="h-4 w-4" />
                </Button>
            </div>

            <Dialog open={editingIndex !== null} onOpenChange={(open) => !open && closeEditDialog()}>
                <DialogContent className="bg-card sm:max-w-sm">
                    <DialogHeader>
                        <DialogTitle>Edit Skill</DialogTitle>
                    </DialogHeader>
                    <div className="flex flex-col gap-4 py-2">
                        <div className="flex flex-col gap-1">
                            <Label className="text-xs text-muted-foreground">Skill Name</Label>
                            <Input
                                autoFocus
                                value={editingName}
                                onChange={(e) => setEditingName(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") { e.preventDefault(); confirmEdit() }
                                    if (e.key === "Escape") closeEditDialog()
                                }}
                            />
                        </div>
                        <div className="flex flex-col gap-1">
                            <Label className="text-xs text-muted-foreground">Weight</Label>
                            <Select
                                value={String(editingWeight)}
                                onValueChange={(v) => setEditingWeight(Number(v))}
                            >
                                <SelectTrigger className="w-full">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-card">
                                    {[1, 2, 3, 4, 5].map((w) => (
                                        <SelectItem key={w} value={String(w)}>{w}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={closeEditDialog}>
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            disabled={!editingName.trim()}
                            onClick={confirmEdit}
                            className="bg-primary hover:bg-primary/90 text-primary-foreground"
                        >
                            Save
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    )
}
